import {Component, computed, effect, inject, OnDestroy, OnInit} from '@angular/core';
import {SidebarComponent} from './components/sidebar/sidebar.component';
import {MainComponent} from './components/main/main.component';
import {TicketsFacade} from '../../features/tickets/facades/tickets.facade';
import {ShortcutContext} from '../../shared/shortcuts/shortcut.context';
import {SidebarNavService} from './components/sidebar/sidebar-nav/sidebar-nav.service';
import {PermissionService} from '../auth/services/permission.service';
import {ROLES_CONFIG, ROLES_DASHBOARD, ROLES_TICKETS, ROLES_USERS} from '../auth/models/user.models';
import {AppContext} from '../app.context';
import {MailboxConfiguratorFacade} from '../../features/mailbox-configurator/mailbox-configurator.facade';

@Component({
	selector: 'app-layout',
	imports: [
		SidebarComponent,
		MainComponent
	],
	standalone: true,
	host: { 'class': 'flex h-screen gap-4 p-3 overflow-auto' },
	template: `
			<app-sidebar></app-sidebar>
			<app-main></app-main>
	`,
	styles: [``]
})
export class AppLayoutComponent implements OnInit, OnDestroy {
	private facade = inject(TicketsFacade)
	private shortcuts = inject(ShortcutContext)
	private navService = inject(SidebarNavService)
	private permission = inject(PermissionService)
	private ctx = inject(AppContext)
	private mailboxFacade = inject(MailboxConfiguratorFacade);

	private registeredShortcuts: string[] = []

	constructor() {
		effect(() => {
			if (this.ctx.appMode() === 'ready') {
				this.facade.loadMergedTickets()
			}
		});
	}

	ngOnInit(): void {
		if (this.permission.canAccess(ROLES_DASHBOARD)) {
			this.shortcuts.register({
				id: 'go-dashboard',
				description: 'Vai alla Dashboard',
				keys: ['MOD', 'D'],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: 'D'}
				],
				action: () => this.navService.openByKey('dashboard'),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.registeredShortcuts.push('go-dashboard')
		}

		if (this.permission.canAccess(ROLES_TICKETS)) {
			this.shortcuts.register({
				id: 'go-no-category',
				description: 'Apri Ticket non categorizzati',
				keys: ['MOD', '-'],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: '-'}
				],
				action: () => this.navService.openByKey('no-category'),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.shortcuts.register({
				id: 'go-support',
				description: 'Vai ai Ticket supporto',
				keys: ['MOD', 'B'],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: 'B'}
				],
				action: () => this.navService.openByKey('support'),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.shortcuts.register({
				id: 'go-other',
				description: 'Vai ai Ticket Altri',
				keys: ['MOD', 'O'],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: 'O'}
				],
				action: () => this.navService.openByKey('other'),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.registeredShortcuts.push('go-no-category', 'go-support', 'go-other')
		}

		if (this.permission.canAccess(ROLES_USERS)) {
			this.shortcuts.register({
				id: 'go-user',
				description: 'Vai agli Utenti',
				keys: ['MOD', 'U'],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: 'U'}
				],
				action: () => this.navService.openByKey('users'),
				enabled: computed(() => this.permission.canAccess(ROLES_USERS)),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.registeredShortcuts.push('go-user')
		}

		if (this.permission.canAccess(ROLES_CONFIG)) {
			this.shortcuts.register({
				id: 'go-mailbox-configurator',
				description: 'Apri il configuratore delle Mailbox',
				keys: ['MOD', ','],
				display: [
					{kind: 'modifier', value: 'MOD'},
					{kind: 'key', value: ','}
				],
				action: () => this.navService.openByKey('mailbox-configurator'),
				enabled: computed(() => this.permission.canAccess(ROLES_CONFIG)),
				showInHelp: true,
				section: 'Navigazione',
				scope: 'global'
			});

			this.registeredShortcuts.push('go-mailbox-configurator')
		}
	}

	ngOnDestroy() {
		this.clearShortcuts()
	}

	private clearShortcuts() {
		this.registeredShortcuts.forEach(id => this.shortcuts.unregister(id))
		this.registeredShortcuts = []
	}
}
